import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import { Badge, Button, Card, DL, ErrorBanner, FilterSelect, PageHeader, Pagination, SearchBox, Spinner, Table } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime, humanize, inr } from '../../lib/format';
import { useAction, useUrlState, type Paged } from '../../lib/hooks';

interface Row { id: string; method: string; status: string; amountPaise: number; amountCapturedPaise: number; amountRefundedPaise: number; createdAt: string; order: { orderNumber: string; status: string }; latestAttempt: { razorpayPaymentId: string | null; method: string | null; status: string; errorDescription: string | null } | null; attemptCount: number; refundCount: number }
const DEFAULTS = { q: '', status: '', method: '', page: '1' };

export function Payments() {
  const nav = useNavigate();
  const [f, setF] = useUrlState(DEFAULTS);
  const query = { ...f, pageSize: 25 };
  const q = useQuery({ queryKey: ['payments', query], queryFn: () => api<Paged<Row>>('/admin/payments', { query }), placeholderData: keepPreviousData });
  return (
    <>
      <PageHeader title="Payments" />
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchBox value={f.q} onChange={(v) => setF({ q: v })} placeholder="Order no. or Razorpay ID" />
          <FilterSelect label="Status" value={f.status} onChange={(v) => setF({ status: v })} options={[{ value: '', label: 'All statuses' }, ...['PENDING', 'CAPTURED', 'FAILED', 'EXPIRED', 'CANCELLED', 'REFUND_INITIATED', 'PARTIALLY_REFUNDED', 'REFUNDED', 'DISPUTED'].map((s) => ({ value: s, label: humanize(s) }))]} />
          <FilterSelect label="Method" value={f.method} onChange={(v) => setF({ method: v })} options={[{ value: '', label: 'Online & COD' }, { value: 'RAZORPAY', label: 'Online' }, { value: 'COD', label: 'COD' }]} />
        </div>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <>
            <Table rows={q.data!.items} rowKey={(p) => p.id} onRowClick={(p) => nav(`/payments/${p.id}`)} columns={[
              { header: 'Order', cell: (p) => <span className="font-medium text-forest-800">{p.order.orderNumber}</span> },
              { header: 'Created', cell: (p) => dateTime(p.createdAt) },
              { header: 'Method', cell: (p) => (p.method === 'COD' ? 'Cash on delivery' : humanize(p.latestAttempt?.method ?? 'Online')) },
              { header: 'Status', cell: (p) => <div><Badge status={p.status} />{p.latestAttempt?.errorDescription && p.status === 'FAILED' && <p className="mt-0.5 text-xs text-danger">{p.latestAttempt.errorDescription}</p>}</div> },
              { header: 'Attempts', cell: (p) => p.attemptCount, className: 'text-right' },
              { header: 'Amount', cell: (p) => inr(p.amountPaise), className: 'text-right' },
              { header: 'Refunded', cell: (p) => (p.amountRefundedPaise ? inr(p.amountRefundedPaise) : '—'), className: 'text-right' },
            ]} />
            <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
          </>
        )}
      </Card>
    </>
  );
}

interface Detail {
  id: string; method: string; status: string; amountPaise: number; amountCapturedPaise: number; amountRefundedPaise: number; isDisputed: boolean; capturedAt: string | null; createdAt: string;
  order: { id: string; orderNumber: string; status: string; totalPaise: number };
  attempts: { id: string; attemptNumber: number; status: string; amountPaise: number; razorpayOrderId: string | null; razorpayPaymentId: string | null; method: string | null; errorDescription: string | null; createdAt: string }[];
  refunds: { id: string; amountPaise: number; status: string; reason: string; isAutomatic: boolean; razorpayRefundId: string | null; manualMethod: string | null; externalReference: string | null; createdAt: string }[];
  events: { id: string; source: string; type: string; fromStatus: string | null; toStatus: string | null; createdAt: string }[];
}

export function PaymentDetail() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['payment', id], queryFn: () => api<Detail>(`/admin/payments/${id}`) });
  const reconcile = useAction((attemptId: string) => api<{ result?: string }>(`/admin/payments/attempts/${attemptId}/reconcile`, { method: 'POST' }), { success: 'Checked with Razorpay — status updated', invalidate: [['payment', id], ['payments']] });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBanner error={q.error} />;
  const p = q.data!;
  return (
    <>
      <Link to="/payments" className="mb-2 inline-flex items-center gap-1 text-sm text-forest-800 hover:underline"><ArrowLeft className="h-4 w-4" />Payments</Link>
      <PageHeader title={`Payment for ${p.order.orderNumber}`} actions={<><Badge status={p.status} />{p.isDisputed && <Badge tone="red">Disputed</Badge>}<Link to={`/orders/${p.order.id}`}><Button variant="secondary">Open order</Button></Link></>} />
      <div className="space-y-4">
        <Card><DL items={[['Method', p.method === 'COD' ? 'Cash on delivery' : 'Razorpay'], ['Amount', inr(p.amountPaise)], ['Received', inr(p.amountCapturedPaise)], ['Refunded', inr(p.amountRefundedPaise)], ['Received at', dateTime(p.capturedAt)]]} /></Card>
        {p.method === 'RAZORPAY' && (
          <Card title="Attempts">
            <p className="mb-2 text-xs text-ink-muted">Payment status only ever comes from Razorpay (verified webhooks or a server-side check). There is no manual “mark as paid”.</p>
            <Table rows={p.attempts} rowKey={(a) => a.id} columns={[
              { header: '#', cell: (a) => a.attemptNumber },
              { header: 'Razorpay IDs', cell: (a) => <div className="font-mono text-xs"><div>{a.razorpayOrderId}</div><div>{a.razorpayPaymentId ?? '—'}</div></div> },
              { header: 'Method', cell: (a) => humanize(a.method ?? '—') },
              { header: 'Status', cell: (a) => <div><Badge status={a.status} />{a.errorDescription && <p className="text-xs text-danger">{a.errorDescription}</p>}</div> },
              { header: 'Started', cell: (a) => dateTime(a.createdAt) },
              { header: '', cell: (a) => can('payments.reconcile') && !['CAPTURED', 'FAILED'].includes(a.status) && <Button variant="secondary" loading={reconcile.isPending && reconcile.variables === a.id} onClick={() => reconcile.mutate(a.id)}><RefreshCw className="h-4 w-4" />Check with Razorpay</Button> },
            ]} />
          </Card>
        )}
        <Card title="Refunds">
          <Table rows={p.refunds} rowKey={(r) => r.id} empty="No refunds." columns={[
            { header: 'Requested', cell: (r) => dateTime(r.createdAt) },
            { header: 'Amount', cell: (r) => inr(r.amountPaise), className: 'text-right' },
            { header: 'Status', cell: (r) => <Badge status={r.status} /> },
            { header: 'Reason', cell: (r) => <span>{r.reason}{r.isAutomatic && <Badge tone="grey">automatic</Badge>}</span> },
            { header: 'Reference', cell: (r) => <span className="font-mono text-xs">{r.razorpayRefundId ?? (r.manualMethod ? `${humanize(r.manualMethod)} ${r.externalReference}` : '—')}</span> },
          ]} />
        </Card>
        <Card title="Event log">
          <Table rows={p.events} rowKey={(e) => e.id} columns={[
            { header: 'When', cell: (e) => dateTime(e.createdAt) },
            { header: 'Source', cell: (e) => humanize(e.source) },
            { header: 'Event', cell: (e) => humanize(e.type) },
            { header: 'Change', cell: (e) => (e.toStatus ? `${e.fromStatus ? humanize(e.fromStatus) + ' → ' : ''}${humanize(e.toStatus)}` : '—') },
          ]} />
        </Card>
      </div>
    </>
  );
}

interface Hook { id: string; eventId: string; eventType: string; status: string; attempts: number; lastError: string | null; receivedAt: string; processedAt: string | null }
export function Webhooks() {
  const [f, setF] = useUrlState({ status: '', page: '1' });
  const q = useQuery({ queryKey: ['webhooks', f], queryFn: () => api<Paged<Hook>>('/admin/webhooks', { query: { ...f, pageSize: 50 } }), placeholderData: keepPreviousData });
  const retry = useAction((id: string) => api(`/admin/webhooks/${id}/retry`, { method: 'POST' }), { success: 'Queued for reprocessing', invalidate: [['webhooks']] });
  return (
    <>
      <PageHeader title="Razorpay webhooks" subtitle="Every event Razorpay sent, verified and processed exactly once." />
      <Card>
        <div className="mb-3"><FilterSelect label="Status" value={f.status} onChange={(v) => setF({ status: v })} options={[{ value: '', label: 'All' }, ...['PROCESSED', 'FAILED', 'RECEIVED', 'PROCESSING', 'IGNORED'].map((s) => ({ value: s, label: humanize(s) }))]} /></div>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <>
            <Table rows={q.data!.items} rowKey={(w) => w.id} columns={[
              { header: 'Received', cell: (w) => dateTime(w.receivedAt) },
              { header: 'Event', cell: (w) => <div><div>{w.eventType}</div><div className="font-mono text-xs text-ink-muted">{w.eventId}</div></div> },
              { header: 'Status', cell: (w) => <div><Badge status={w.status} />{w.lastError && <p className="text-xs text-danger">{w.lastError}</p>}</div> },
              { header: 'Tries', cell: (w) => w.attempts, className: 'text-right' },
              { header: '', cell: (w) => w.status === 'FAILED' && <Button variant="secondary" onClick={() => retry.mutate(w.id)}>Retry</Button> },
            ]} />
            <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
          </>
        )}
      </Card>
    </>
  );
}
